import React, { FC, PropsWithChildren, ReactNode, RefObject, useCallback } from 'react';
import { i18n } from '../../../i18n/translate';
import { VisuallyHidden, Dialog, Drawer, Portal, CloseButton } from '@chakra-ui/react';
import { Button as CoreButton, ButtonVariant } from '../Button';
import { ModalSize, ModalType, ModalVariant, SlideDirection } from './types';

/** `SlideDirection` -> drawer `placement`. */
const DRAWER_PLACEMENT_MAP: Record<SlideDirection, 'start' | 'end' | 'top' | 'bottom'> = {
  left: 'start',
  right: 'end',
  top: 'top',
  bottom: 'bottom',
};

/** Dialog.Root/Drawer.Root types know only the built-in recipes' variants, and there is no recipe-aware
 * typegen, so Modal.ts's custom `overflow` variant is added here in one cast rather than at each call site. */
const DialogRootWithOverflow = Dialog.Root as FC<React.ComponentProps<typeof Dialog.Root> & { overflow?: boolean }>;
const DrawerRootWithOverflow = Drawer.Root as FC<React.ComponentProps<typeof Drawer.Root> & { overflow?: boolean }>;

export interface ModalProps extends PropsWithChildren {
  id?: string;
  isOpen: boolean | false;
  onClose?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  role?: string;
  size?: ModalSize;
  modalType: ModalType;
  showCloseButton?: boolean;
  drawerPlacement?: SlideDirection;
  variant?: ModalVariant;
  isCentered?: boolean;
  isDestructive?: boolean;
  closeLabel?: string;
  actionLabel?: string;
  actionOnClick?: () => void;
  actionLoading?: boolean;
  onExitComplete?: () => void;
  className?: string;
  containerRef?: React.RefObject<HTMLElement | null>;
  initialFocusEl?: () => HTMLElement | null;
}

export const Modal: FC<ModalProps> = ({
  id,
  modalType = 'modal',
  header,
  footer,
  size = 'lg',
  showCloseButton = true,
  isOpen,
  onClose,
  drawerPlacement = 'left',
  variant,
  isCentered,
  children,
  isDestructive,
  closeLabel = 'Close',
  actionLabel,
  actionOnClick,
  actionLoading,
  onExitComplete,
  className,
  containerRef = undefined,
  initialFocusEl,
}) => {
  /**
   * Methods
   */
  const handleOnClose = useCallback(() => {
    if (!onClose) {
      return;
    }
    onClose();
  }, [onClose]);

  const handleOpenChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!open) {
        handleOnClose();
      }
    },
    [handleOnClose]
  );

  /** Ark's own focus-trap defers initial focus to a `requestAnimationFrame` tick; focus it synchronously too. */
  React.useLayoutEffect(() => {
    if (isOpen) {
      initialFocusEl?.()?.focus();
    }
  }, [isOpen, initialFocusEl]);

  const actionButtonRef = React.useRef<HTMLButtonElement>(null) as RefObject<HTMLButtonElement>;
  type TitleComponent = React.ComponentType<{ children?: ReactNode; asChild?: boolean }>;

  // `Title` renders an `h2` itself (global `h2` CSS styles it), so strings pass through unwrapped
  // (avoids an `h2`-in-`h2` nest); a ReactNode header is usually already a heading, merged via `asChild`.
  const renderHeaderContent = (Title: TitleComponent, content: ReactNode) => {
    if (typeof content === 'string' || typeof content === 'number') {
      return <Title>{content}</Title>;
    }

    return <Title asChild>{content}</Title>;
  };

  const renderFallbackHeader = (Title: TitleComponent) => (
    <Title>
      <VisuallyHidden>{i18n.t('Alert')}</VisuallyHidden>
    </Title>
  );

  /**
   * DOM
   */
  if (modalType === 'drawer') {
    return (
      <DrawerRootWithOverflow
        {...(id ? { id } : {})}
        open={isOpen}
        onOpenChange={handleOpenChange}
        // Chakra's generated recipe types carry the stock size union, but our dialog/drawer recipes declare the
        // full xs-6xl scale, so the value is wider than the type knows (as with Button's custom `compact`).
        size={size as never}
        placement={DRAWER_PLACEMENT_MAP[drawerPlacement]}
        overflow={variant === 'overflow'}
        onExitComplete={onExitComplete}
      >
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header as="header" data-object-type="core-drawer-header">
                {header ? renderHeaderContent(Drawer.Title, header) : renderFallbackHeader(Drawer.Title)}
              </Drawer.Header>
              {showCloseButton && (
                <Drawer.CloseTrigger asChild>
                  <CloseButton aria-label={closeLabel} data-object-type="core-drawer-close-button" />
                </Drawer.CloseTrigger>
              )}
              <Drawer.Body data-object-type="core-drawer-body">{children}</Drawer.Body>
              {footer && (
                <Drawer.Footer as="footer" data-object-type="core-drawer-footer">
                  {footer}
                </Drawer.Footer>
              )}
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </DrawerRootWithOverflow>
    );
  }
  if (modalType === 'alert') {
    return (
      <Dialog.Root
        role="alertdialog"
        closeOnInteractOutside={false}
        placement="center"
        size="md"
        {...(id ? { id } : {})}
        open={isOpen}
        onOpenChange={handleOpenChange}
        onExitComplete={onExitComplete}
        initialFocusEl={() => actionButtonRef.current}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content data-object-type="core-modal-content">
              <Dialog.Header as="header">
                {header ? renderHeaderContent(Dialog.Title, header) : renderFallbackHeader(Dialog.Title)}
              </Dialog.Header>
              <Dialog.Body data-testid="core-modal-body" data-object-type="core-modal-body">
                {children}
              </Dialog.Body>
              <Dialog.Footer as="footer" data-testid="core-modal-footer" data-object-type="core-modal-footer">
                <CoreButton
                  variant={ButtonVariant.SOLID}
                  colorScheme={'gray'}
                  onClick={handleOnClose}
                  ref={actionButtonRef}
                >
                  {closeLabel}
                </CoreButton>
                {actionLabel && (
                  <CoreButton
                    variant={ButtonVariant.SOLID}
                    colorScheme={isDestructive ? 'error' : 'brand'}
                    onClick={isDestructive ? actionOnClick : actionOnClick || onClose}
                    loading={actionLoading}
                  >
                    {actionLabel}
                  </CoreButton>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    );
  }

  return (
    <DialogRootWithOverflow
      {...(id ? { id } : {})}
      open={isOpen}
      onOpenChange={handleOpenChange}
      // See the drawer branch above: our recipe's size scale is wider than the stock types.
      size={size as never}
      motionPreset={'none'}
      overflow={variant === 'overflow'}
      onExitComplete={onExitComplete}
      initialFocusEl={initialFocusEl}
      // `isCentered` doesn't centre the dialog: it only locks scroll and scrolls the body inside.
      {...(isCentered === true ? ({ scrollBehavior: 'inside' } as const) : {})}
    >
      <Portal container={containerRef}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className={className} data-object-type="core-modal-content">
            <Dialog.Header as="header" data-testid="core-modal-header" data-object-type="core-modal-header">
              {header ? renderHeaderContent(Dialog.Title, header) : renderFallbackHeader(Dialog.Title)}
            </Dialog.Header>
            {showCloseButton && (
              <Dialog.CloseTrigger asChild>
                <CloseButton aria-label={closeLabel} data-testid="core-drawer-modal-button" />
              </Dialog.CloseTrigger>
            )}
            <Dialog.Body data-testid="core-modal-body" data-object-type="core-modal-body">
              {children}
            </Dialog.Body>
            {footer && (
              <Dialog.Footer as="footer" data-testid="core-modal-footer" data-object-type="core-modal-footer">
                {footer}
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </DialogRootWithOverflow>
  );
};
